import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createProjectSprintTask } from '@services/task/task.service';
import { invalidateProjectTaskQueries } from '@hooks/projects/projectQueryInvalidation';

function normalizeBinaryFiles(files) {
    if (!Array.isArray(files)) return [];
    return files
        .map((f) => f?.originFileObj ?? f?.file ?? f)
        .filter((f) => f instanceof File || f instanceof Blob);
}

function normalizeLinkAttachments(attachments) {
    if (!Array.isArray(attachments)) return [];
    return attachments
        .filter((att) => att && att.url)
        .filter((att) => {
            const type = String(att.type || 'link').toLowerCase();
            return type === 'link';
        })
        .map((att) => ({
            type: 'link',
            url: att.url,
            ...(att.title ? { title: att.title } : {}),
        }));
}

/**
 * Hook for creating project tasks. Supports both sprint-based and project-wide tasks.
 */
export function useProjectTask(projectId) {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: (body) => {
            const files = normalizeBinaryFiles(body.files);
            const linkAttachments = normalizeLinkAttachments(body.attachments);
            if (files?.length) {
                const formData = new FormData();
                formData.append('sprintId', body.sprintId ?? '');
                formData.append('title', typeof body.title === 'string' ? body.title : '');
                if (body.milestoneId) formData.append('milestoneId', body.milestoneId);
                if (body.phaseId) formData.append('phaseId', body.phaseId);
                if (body.description) formData.append('description', JSON.stringify(body.description));
                if (body.type) formData.append('type', body.type);
                if (body.priority) formData.append('priority', body.priority);
                if (body.assigneeId) formData.append('assigneeId', body.assigneeId);
                if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                files.forEach((file) => formData.append('files', file));
                return createProjectSprintTask(projectId, formData);
            }
            const { files: _f, attachments: _a, ...rest } = body;
            return createProjectSprintTask(projectId, { ...rest, ...(linkAttachments.length > 0 && { attachments: linkAttachments }) });
        },
        onSuccess: () => {
            invalidateProjectTaskQueries(queryClient, projectId);
        },
    });

    const createTask = (body, callbacks = {}) => {
        mutation.mutate(body, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    return {
        createTask,
        createTaskPending: mutation.isPending,
    };
}
